import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import {
  collectionGroup,
  onSnapshot
} from 'firebase/firestore';
import '../styles/MessagesPage.css';
import '../styles/ClientList.css';
import '../styles/ViewOrderPage.css';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';

function groupByDate(messages) {
  const grouped = {};
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();

  messages.forEach(msg => {
    const dateStr = msg.createdAt?.toDate?.().toDateString?.() ?? 'Unknown';
    let label = dateStr;

    if (dateStr === today) label = 'Today';
    else if (dateStr === yesterday) label = 'Yesterday';

    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(msg);
  });

  return grouped;
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const { clients, allOrders } = useAppData();
  const [messages, setMessages] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'orders', 'general'
  const [senderFilter, setSenderFilter] = useState('all'); // 'all' or a senderId
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [isLoading, setIsLoading] = useState(true);

  // Build lookup maps from cached data - instant, no Firestore calls
  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);
  const orderMap = useMemo(() => Object.fromEntries(allOrders.map(o => [o.id, o])), [allOrders]);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      setCurrentUserId(user.uid);
    } else {
      console.warn('No authenticated user found.');
    }

    const unsubscribe = onSnapshot(collectionGroup(db, 'messages'), snap => {
      setIsLoading(true);
      
      const raw = snap.docs
        .map(d => {
          const parentColl = d.ref.parent;
          const clientDocRef = parentColl.parent;
          if (!clientDocRef) return null;
          return {
            id: d.id,
            clientId: clientDocRef.id,
            ...d.data()
          };
        })
        .filter(x => x !== null);

      // Enrich messages using cached client and order maps - no Firestore calls
      const withNames = raw.map(msg => {
        const clientData = clientMap[msg.clientId] || {};
        const name = clientData.name || clientData.clientName || clientData.dealershipName || 'Unknown';

        let enhancedMsg = {
          ...msg,
          dealershipName: name,
          recipientType: msg.recipientType?.toLowerCase() ?? '',
          senderRole: msg.senderRole?.toLowerCase() ?? '',
          clientName: name,
        };

        // Fill in order details from cache if missing
        if (msg.orderId && (!msg.orderNumber || !msg.campaignName)) {
          const orderData = orderMap[msg.orderId];
          if (orderData) {
            enhancedMsg = {
              ...enhancedMsg,
              orderNumber: enhancedMsg.orderNumber || orderData.jobNumber || 'Unknown',
              campaignName: enhancedMsg.campaignName || orderData.campaignName || 'Campaign',
            };
          }
        }

        return enhancedMsg;
      });

      const filtered = withNames
        .filter(msg => {
          const recipient = msg.recipientType?.toLowerCase?.() ?? '';
          const isInternal = recipient === 'internal' && !msg.read;
          const isFromClient = recipient === 'admin' && !msg.read;
          const isSentByCurrentUser = msg.senderId === currentUserId;

          // Only show unread internal/client messages that were not sent by the current user
          return !isSentByCurrentUser && (isInternal || isFromClient);
        })
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      setMessages(filtered);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId, clientMap, orderMap]);

  // Use React Router navigation - no full page reload
  const handleMessageClick = (msg) => {
    if (msg.orderId) {
      navigate(`/orders/${msg.orderId}`);
    } else {
      navigate(`/clients/${msg.clientId}`);
    }
  };

  // Enhanced function to get message display title
  const getMessageTitle = (msg) => {
    // Check for order-related message (has orderId or orderNumber)
    if (msg.orderId || msg.orderNumber) {
      const orderNum = msg.orderNumber || 'Unknown Order';
      const campaign = msg.campaignName || 'Campaign';
      const dealer = msg.dealershipName || 'Dealer';
      return `${orderNum} - ${campaign} - ${dealer}`;
    }
    
    // For client profile messages (no order association)
    return msg.dealershipName || msg.clientName || 'Client';
  };

  // Get sender information for display
  const getSenderInfo = (msg) => {
    const senderName = msg.senderName || 'Unknown';
    const senderRole = msg.senderRole || 'user';
    
    if (senderName === 'System' || senderRole === 'system') {
      return '🔔 System';
    }
    
    // Show sender name and role
    return `${senderName}`;
  };

  // Get message category for filtering
  const getMessageCategory = (msg) => {
    return (msg.orderId || msg.orderNumber || msg.isOrderMessage) ? 'order' : 'general';
  };

  // Build unique sender list from current messages
  const uniqueSenders = useMemo(() => {
    const seen = new Map();
    messages.forEach(msg => {
      if (msg.senderId && !seen.has(msg.senderId)) {
        seen.set(msg.senderId, msg.senderName || 'Unknown');
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [messages]);

  // Get counts for filter buttons
  const totalMessages = messages.length;
  const orderMessages = messages.filter(msg => getMessageCategory(msg) === 'order').length;
  const generalMessages = totalMessages - orderMessages;

  // Apply category + sender filters
  const filteredMessages = messages.filter(msg => {
    if (filter === 'orders' && getMessageCategory(msg) !== 'order') return false;
    if (filter === 'general' && getMessageCategory(msg) !== 'general') return false;
    if (senderFilter !== 'all' && msg.senderId !== senderFilter) return false;
    return true;
  });

  // Sort filtered messages by date
  const sortedMessages = [...filteredMessages].sort((a, b) => {
    const diff = (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
    return sortOrder === 'asc' ? diff : -diff;
  });

  const grouped = groupByDate(sortedMessages);

  if (isLoading) {
    return (
      <div className="client-page-messages">
        <div className="gradient-text-smaller">
          <h1>All Messages</h1>
        </div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="client-page-messages">
      <div className="gradient-text-smaller">
        <h1>All Messages</h1>
      </div>

      {/* Filter Controls - single bar */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        padding: '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: '500', color: '#495057' }}>Filter by:</span>

        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            backgroundColor: filter === 'all' ? '#007bff' : 'white',
            color: filter === 'all' ? 'white' : '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s ease'
          }}
        >
          All Messages ({totalMessages})
        </button>

        <button
          onClick={() => setFilter('orders')}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            backgroundColor: filter === 'orders' ? '#007bff' : 'white',
            color: filter === 'orders' ? 'white' : '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s ease'
          }}
        >
          📋 Order Messages ({orderMessages})
        </button>

        <button
          onClick={() => setFilter('general')}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            backgroundColor: filter === 'general' ? '#007bff' : 'white',
            color: filter === 'general' ? 'white' : '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s ease'
          }}
        >
          💬 General Messages ({generalMessages})
        </button>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#dee2e6', margin: '0 4px' }} />

        {/* Sender dropdown */}
        <select
          value={senderFilter}
          onChange={e => setSenderFilter(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: `1px solid ${senderFilter !== 'all' ? '#6c757d' : '#dee2e6'}`,
            backgroundColor: senderFilter !== 'all' ? '#6c757d' : 'white',
            color: senderFilter !== 'all' ? 'white' : '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          <option value="all">👤 All Senders</option>
          {uniqueSenders.map(({ id, name }) => (
            <option key={id} value={id}>
              {name} ({messages.filter(m => m.senderId === id).length})
            </option>
          ))}
        </select>

        {/* Sort toggle */}
        <button
          onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            backgroundColor: 'white',
            color: '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s ease'
          }}
        >
          {sortOrder === 'desc' ? '⬇️ Newest First' : '⬆️ Oldest First'}
        </button>
      </div>

      {sortedMessages.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#6c757d',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px' 
        }}>
          {filter === 'all' && senderFilter === 'all' ? (
            <p>No messages to display.</p>
          ) : (
            <p>No messages match the current filters. Try adjusting your selection.</p>
          )}
        </div>
      ) : (
        <ul className="message-list">
          {Object.entries(grouped).map(([dateLabel, msgs]) => (
            <div key={dateLabel}>
              <div className="sticky-date">{dateLabel}</div>
              {msgs.map(msg => {
                const category = getMessageCategory(msg);
                const isOrderMessage = category === 'order';
                
                return (
                  <li
                    key={msg.id}
                    className="hoverable fade-in"
                    onClick={() => handleMessageClick(msg)}
                    style={{
                      opacity: msg.read ? 0.6 : 1,
                      padding: '16px',
                      marginBottom: '12px',
                      border: `1px solid ${isOrderMessage ? '#007bff' : '#ccc'}`,
                      borderLeft: `4px solid ${isOrderMessage ? '#007bff' : '#6c757d'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      animation: 'fadeIn 0.5s ease',
                      textDecoration: 'none',
                      color: 'inherit',
                      listStyle: 'none',
                      backgroundColor: msg.read ? '#f8f9fa' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Message Header */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <div style={{ flex: 1 }}>
                        {/* Sender Information - Most Prominent */}
                        <div style={{ 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#212529',
                          marginBottom: '4px'
                        }}>
                          {getSenderInfo(msg)}
                        </div>
                        
                        {/* Context Information - Secondary */}
                        <div style={{
                          fontSize: '14px',
                          color: isOrderMessage ? '#007bff' : '#6c757d',
                          marginBottom: '4px'
                        }}>
                          {isOrderMessage ? '📋 ' : '💬 '}{getMessageTitle(msg)}
                        </div>
                        
                        {/* Message category badge */}
                        <div style={{ marginTop: '4px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: isOrderMessage ? '#e7f1ff' : '#e9ecef',
                            color: isOrderMessage ? '#0056b3' : '#495057'
                          }}>
                            {isOrderMessage ? 'Order Message' : 'General Message'}
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '8px'
                      }}>
                        <small style={{ color: '#6c757d', fontSize: '12px' }}>
                          {msg.createdAt?.toDate?.().toLocaleString() || 'Unknown time'}
                        </small>
                        
                        {/* Read status indicator (no checkbox) */}
                        <span style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          fontStyle: 'italic'
                        }}>
                          {msg.read ? 'Read' : 'Unread'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Message Content */}
                    <p style={{ 
                      marginTop: '8px', 
                      color: '#495057',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      maxHeight: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {msg.text}
                    </p>
                    
                    {/* Image Thumbnail Preview */}
                    {(() => {
                      // Get image attachments from the attachments array
                      const imageAttachments = (msg.attachments || []).filter(att => att.category === 'image');
                      
                      // Only check legacy if no attachments array exists
                      const hasLegacyImage = imageAttachments.length === 0 && msg.isImage && msg.attachmentUrl;
                      
                      const hasImages = imageAttachments.length > 0 || hasLegacyImage;
                      
                      if (!hasImages) return null;
                      
                      const totalImages = imageAttachments.length || (hasLegacyImage ? 1 : 0);
                      
                      return (
                        <div style={{
                          marginTop: '12px',
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          {/* Show legacy single image ONLY if no attachments array */}
                          {hasLegacyImage && (
                            <img
                              src={msg.attachmentUrl}
                              alt={msg.attachmentName || "Message attachment"}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                            />
                          )}
                          
                          {/* Show all image attachments from attachments array */}
                          {imageAttachments.map((att, idx) => (
                            <img
                              key={idx}
                              src={att.url}
                              alt={att.name || `Image ${idx + 1}`}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                            />
                          ))}
                          
                          <div style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            display: 'flex',
                            alignItems: 'center',
                            fontStyle: 'italic'
                          }}>
                            📎 {totalImages} image{totalImages !== 1 ? 's' : ''} attached
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Click hint */}
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#6c757d',
                      fontStyle: 'italic'
                    }}>
                      Click to {isOrderMessage ? 'view order' : 'view client profile'} →
                    </div>
                  </li>
                );
              })}
            </div>
          ))}
        </ul>
      )}
    </div>
  );
}